import { CircleDollarSign } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LlmUsageModelTotals } from "@/features/llm-usage/api/llm-usage.api";
import {
  AdminDataPanel,
  AdminDataTable,
  type AdminColumn,
} from "@/features/admin/components/layout";
import type { SortValue } from "@/features/admin/lib/table-sort";
import { ToggleChip } from "@/features/identity-hub/components/dashboard/shared/toolbar/ToggleChip";
import { brandForProvider, humanizeModelId } from "../lib/model-display";
import { isLongTail } from "../lib/table-filters";
import { LongTailChip } from "./LongTailChip";
import { ProviderIcon } from "./ProviderIcon";
import {
  tokenMeasureColumns,
  tokenMeasureSortValue,
  type TokenMeasureKey,
} from "./token-measure-columns";

interface LlmUsageModelTableProps {
  rows: LlmUsageModelTotals[];
  isLoading: boolean;
  /** Rows belong to the previous filter while the next one is in flight. */
  isStale: boolean;
  /** Window total, so each row can show its share of the bill. */
  totalTokens: number;
  /** `tokenUsageLongTailPercent`: the share a row needs to escape the fold. */
  longTailPercent: number;
  /** Fail % a row is marked at, or null when the failure alert does not cover this window. */
  failureAlertPercent?: number | null;
}

export type ModelSortKey = TokenMeasureKey | "model";

/** Spend, matching the order the server already ranked the rows in. */
const DEFAULT_SORT = { key: "totalTokens", dir: "desc" } as const;

/** Provider leads the label, so it leads the sort — it groups the vendors. */
function modelSortValue(row: LlmUsageModelTotals, key: ModelSortKey): SortValue {
  if (key === "model") return `${row.provider} ${humanizeModelId(row.model)}`;
  return tokenMeasureSortValue(row, key);
}

/**
 * The per-model breakdown, ordered by spend until the reader says otherwise —
 * the table answers "which model is the bill" before it answers anything else,
 * so the biggest row is the first one read.
 */
export function LlmUsageModelTable({
  rows,
  isLoading,
  isStale,
  totalTokens,
  longTailPercent,
  failureAlertPercent = null,
}: LlmUsageModelTableProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [unpricedOnly, setUnpricedOnly] = useState(false);
  const [hideLongTail, setHideLongTail] = useState(false);

  // Unpriced narrows first, so the long-tail count reports how many of the rows
  // you are actually looking at would fold away rather than how many exist.
  const pricedFiltered = useMemo(
    () => (unpricedOnly ? rows.filter((row) => !row.isPriced) : rows),
    [rows, unpricedOnly],
  );
  const longTailCount = useMemo(
    () =>
      pricedFiltered.filter((row) => isLongTail(row.totalTokens, totalTokens, longTailPercent))
        .length,
    [pricedFiltered, totalTokens, longTailPercent],
  );
  const visibleRows = useMemo(
    () =>
      hideLongTail
        ? pricedFiltered.filter((row) => !isLongTail(row.totalTokens, totalTokens, longTailPercent))
        : pricedFiltered,
    [pricedFiltered, hideLongTail, totalTokens, longTailPercent],
  );

  const unpricedCount = useMemo(() => rows.filter((row) => !row.isPriced).length, [rows]);

  const columns: AdminColumn<LlmUsageModelTotals, ModelSortKey>[] = [
    {
      id: "model",
      header: t("admin.llmUsage.table.model", "Model"),
      isFill: true,
      isSortable: true,
      // The id stays a hover away rather than on the line: it is the key a rate
      // is set against, but it is not what an operator scanning for the
      // expensive model is reading.
      cellTitle: (row) => row.model,
      cell: (row) => (
        <span className="flex items-center gap-2">
          <ProviderIcon brand={brandForProvider(row.provider)} />
          <span className="font-medium">{humanizeModelId(row.model)}</span>
        </span>
      ),
    },
    ...tokenMeasureColumns<LlmUsageModelTotals>({
      t,
      locale,
      totalTokens,
      includeFailure: true,
      failureAlertPercent,
      includeThinking: true,
      unpricedTokensOf: (row) => (row.isPriced ? 0 : row.totalTokens),
    }),
  ];

  return (
    <AdminDataPanel
      title={t("admin.llmUsage.table.title", "Models")}
      maxRows={10}
      controls={
        <>
          <ToggleChip
            icon={<CircleDollarSign className="h-3.5 w-3.5" />}
            label={t("admin.llmUsage.filters.unpricedOnly", "Needs a rate")}
            active={unpricedOnly}
            onClick={() => setUnpricedOnly((prev) => !prev)}
            count={unpricedCount}
            title={t(
              "admin.llmUsage.filters.unpricedOnlyTitle",
              "Show only models with no rate set, whose cost is missing from every total",
            )}
          />
          <LongTailChip
            active={hideLongTail}
            onToggle={() => setHideLongTail((prev) => !prev)}
            hiddenCount={longTailCount}
            thresholdPercent={longTailPercent}
          />
        </>
      }
    >
      <AdminDataTable
        caption={t("admin.llmUsage.table.caption", "Token usage per model")}
        columns={columns}
        rows={visibleRows}
        getRowId={(row) => `${row.provider}-${row.model}`}
        isLoading={isLoading}
        isStale={isStale}
        isFiltered={visibleRows.length < rows.length}
        emptyMessage={t("admin.llmUsage.table.empty", "No LLM calls in this window")}
        noMatchMessage={t("admin.llmUsage.table.noMatches", "No models match these filters")}
        defaultSort={DEFAULT_SORT}
        sortValue={modelSortValue}
      />
    </AdminDataPanel>
  );
}
